import React, { useCallback, useMemo, useRef, useState } from "react";
import { NumberParam, StringParam, useQueryParam } from "use-query-params";
import { getReportDateRangeLabel } from "@common/helpers/reports";
import { NamedView, View } from "@common/types/views/views";
import {
    Form1099NecView,
    Form1099NecViewSchema,
} from "@common/types/views/form1099NecView";
import { useForm1099NecReportQuery } from "@api/form1099NecReport.api";
import { Button } from "@design-system/Button/Button";
import { Card } from "@design-system/Card/Card";
import { CardSection } from "@design-system/Card/CardSection";
import { Loader } from "@design-system/Loader/Loader";
import { LoaderOnInitialLoading } from "@design-system/LoaderOnInitialLoading/LoaderOnInitialLoading";
import { Picker } from "@design-system/Picker/Picker.lib";
import { CollapseAllButton } from "@design-system/ViewTable/CollapseAllButton";
import { ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { DEFAULT_VIEW_ID } from "@components/views/constants";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { ViewToolbar } from "@components/views/ViewToolbar/ViewToolbar";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import { useIsClassesEnabled } from "@hooks/useIsClassesEnabled";
import { AccountingEntitySelect } from "../AccountingEntitySelect";
import { AccountingReportHeader } from "../AccountingReportHeader";
import { buildLocalReportChrome } from "../localReportChrome";
import { useAccountingTabContext } from "../useAccountingTab";
import { useEntitiesWithAccountingAvailable } from "../useEntitiesWithAccountingAvailable";
import {
    Form1099NecDrawers,
    useForm1099NecDrawers,
} from "./Form1099NecDrawers";
import { Form1099NecExportButton } from "./Form1099NecExportButton";
import { Form1099NecTable } from "./Form1099NecTable";
import { Form1099NecTinExportButton } from "./Form1099NecTinExportButton";
import {
    buildTaxYearItems,
    FORM_1099_NEC_REPORT_LABEL,
    filterForm1099NecRows,
    getDefaultTaxYear,
    getWorkspaceCurrentYear,
    toForm1099NecRows,
} from "./form1099NecFormat";
import { useForm1099ExpandedState } from "./useForm1099ExpandedState";
import {
    FORM_1099_NEC_AVAILABLE_FILTERS,
    FORM_1099_NEC_BOX_OPTIONS,
    FORM_1099_NEC_FILTER_TAB_LABELS,
    FORM_1099_NEC_SOURCE_OPTIONS,
    useForm1099NecFilters,
} from "./useForm1099NecFilters";
import {
    buildForm1099NecConfig,
    buildForm1099NecView,
} from "./form1099NecViewConfig";
import styles from "./Form1099NecPage.module.scss";

export const Form1099NecPage: React.FC = () => {
    const activeWorkspace = useActiveWorkspace();
    if (!activeWorkspace) {
        return <Loader />;
    }
    return (
        <Form1099NecPageContent
            currentYear={getWorkspaceCurrentYear(activeWorkspace.timezone)}
        />
    );
};

interface Form1099NecPageContentProps {
    currentYear: number;
}

const Form1099NecPageContent: React.FC<Form1099NecPageContentProps> = ({
    currentYear,
}) => {
    const { setFilters, selectedEntity } = useAccountingTabContext();
    const availableEntities = useEntitiesWithAccountingAvailable();
    const tableRef = useRef<ViewTableHandle>(null);
    const drawers = useForm1099NecDrawers({
        selectedEntity,
        availableEntities,
    });
    const { mappingEntityIds } = drawers;

    const isClassesEnabled = useIsClassesEnabled();
    const [taxYearParam, setTaxYearParam] = useQueryParam(
        "taxYear",
        NumberParam,
    );
    const [searchParam, setSearchParam] = useQueryParam("search", StringParam);
    const search = searchParam ?? "";
    const taxYearItems = useMemo(
        () =>
            buildTaxYearItems({
                currentYear,
                bookkeepingStartDate: selectedEntity?.bookkeepingStartDate,
            }),
        [currentYear, selectedEntity?.bookkeepingStartDate],
    );
    const defaultTaxYear = getDefaultTaxYear({ taxYearItems, currentYear });
    const taxYear =
        taxYearItems.find((item) => item.value === taxYearParam)?.value ??
        defaultTaxYear;

    const defaultView = useMemo(() => buildForm1099NecView(), []);
    const builtInViews = useMemo<Array<NamedView<Form1099NecView>>>(
        () => [
            {
                ...defaultView,
                id: DEFAULT_VIEW_ID,
                name: "Default",
            },
        ],
        [defaultView],
    );
    const config = useMemo(
        () => buildForm1099NecConfig({ isClassesEnabled }),
        [isClassesEnabled],
    );
    const [cachedView, setCachedView] = useWorkspaceUserCache<Form1099NecView>(
        WORKSPACE_USER_CACHE_KEYS.FORM_1099_NEC_PAGE_VIEW_CACHE_KEY,
        Form1099NecViewSchema,
    );
    const [view, setView] = useState<Form1099NecView>(
        cachedView ?? defaultView,
    );
    const handleViewChange = useCallback(
        (newView: View) => {
            const parsed = Form1099NecViewSchema.safeParse(newView);
            const nextView = parsed.success ? parsed.data : defaultView;
            setView(nextView);
            setCachedView(nextView);
        },
        [defaultView, setCachedView],
    );

    const {
        headerFilters,
        reportFilters,
        isLoadingReportFilters,
        hasActiveFilters,
        onFiltersChange,
    } = useForm1099NecFilters({
        view,
        onViewChange: handleViewChange,
        entityId: selectedEntity?.id,
    });

    const report = useForm1099NecReportQuery({
        taxYear,
        entityId: selectedEntity?.id,
        filters: reportFilters,
    });
    const { data, isSuccess } = report;

    const allRows = useMemo(
        () => toForm1099NecRows(data?.entities ?? []),
        [data],
    );
    const rows = useMemo(
        () => filterForm1099NecRows(allRows, search),
        [allRows, search],
    );
    const { expanded, setExpanded } = useForm1099ExpandedState(allRows);

    const chrome = useMemo(
        () =>
            buildLocalReportChrome({
                title: FORM_1099_NEC_REPORT_LABEL,
                subtitle: selectedEntity?.name ?? "",
                subtitle2: data
                    ? getReportDateRangeLabel(
                          new Date(data.taxYear, 0, 1),
                          new Date(data.taxYear, 11, 31),
                      )
                    : "",
                footer: "",
            }),
        [data, selectedEntity?.name],
    );

    return (
        <>
            <ViewToolbar<Form1099NecView>
                view={view}
                onViewChange={handleViewChange}
                viewConfig={config}
                builtInViews={builtInViews}
                defaultFilters={defaultView.filters}
                baseViewCacheKey={
                    WORKSPACE_USER_CACHE_KEYS.FORM_1099_NEC_PAGE_BASE_VIEW_ID_CACHE_KEY
                }
            >
                <CollapseAllButton expanded={expanded} tableRef={tableRef} />
                <Form1099NecExportButton
                    taxYear={taxYear}
                    entityId={selectedEntity?.id}
                    disabled={!isSuccess}
                />
                <Form1099NecTinExportButton
                    taxYear={taxYear}
                    entityId={selectedEntity?.id}
                    disabled={!isSuccess}
                />
            </ViewToolbar>
            <TablePageHeader
                search={search}
                onSearchChange={(value) =>
                    setSearchParam(value === "" ? undefined : value)
                }
                searchPlaceholder="Search recipients or transactions…"
                filters={headerFilters}
                onFiltersChange={onFiltersChange}
                defaultFilters={defaultView.filters}
                availableFilters={FORM_1099_NEC_AVAILABLE_FILTERS}
                tabLabels={FORM_1099_NEC_FILTER_TAB_LABELS}
                selectionLabelVariant="summary"
                journalEntrySourceOptions={FORM_1099_NEC_SOURCE_OPTIONS}
                counterpartyEntityLabel="Vendor"
                directionSwitchDisabled
                valuePickersOnly
                form1099BoxOptions={FORM_1099_NEC_BOX_OPTIONS}
                prependChildren={
                    <Picker.Select
                        items={taxYearItems}
                        value={taxYear}
                        onValueChange={(year) =>
                            setTaxYearParam(year ?? undefined)
                        }
                    />
                }
                isFullPage
            >
                <AccountingEntitySelect
                    className={styles.entitySelect}
                    entities={availableEntities}
                    selectedEntity={selectedEntity}
                    onChange={(entity) =>
                        setFilters((current) => ({
                            ...current,
                            entityId: entity?.id,
                        }))
                    }
                />
                <Button
                    type="button"
                    variant="primary"
                    size="md"
                    disabled={mappingEntityIds.length === 0}
                    onClick={drawers.openMappingDrawer}
                >
                    View mapping
                </Button>
            </TablePageHeader>
            <LoaderOnInitialLoading
                queryResult={{
                    ...report,
                    isLoading:
                        report.isLoading ||
                        (isLoadingReportFilters && !report.data),
                }}
                dimWhileFetching
            >
                {data && (
                    <Card stacked sharpEdges fullPage>
                        <CardSection className={styles.header}>
                            <AccountingReportHeader chrome={chrome} />
                        </CardSection>
                        <Form1099NecTable
                            tableRef={tableRef}
                            expanded={expanded}
                            onExpandedChange={setExpanded}
                            rows={rows}
                            isFiltered={search.length > 0 || hasActiveFilters}
                            onViewVendors={drawers.openCounterpartiesDrawer}
                            onUpdateMapping={
                                mappingEntityIds.length === 0
                                    ? undefined
                                    : drawers.openMappingDrawer
                            }
                            view={view}
                            config={config}
                            onViewChange={handleViewChange}
                        />
                    </Card>
                )}
            </LoaderOnInitialLoading>
            <Form1099NecDrawers drawers={drawers} />
        </>
    );
};
